<template>
  <div>
    <transition name="dg-backdrop" appear @after-leave="animationEnded('backdrop')">
      <div v-if="show" class="dg-backdrop" />
    </transition>

    <transition :name="animation" @after-leave="animationEnded('content')" appear>
      <div v-if="show"
           @click="closeAtOutsideClick"
           :class="['dg-container', {'dg-container--has-input': (isHardConfirm || isPrompt)}]"
      >
        <div class="dg-content-cont dg-content-cont--floating">
          <div class="dg-main-content"
               :class="customClass.mainContent"
               @click.stop
          >
            <div :class="['dg-content-body', customClass.body, {'dg-content-body--has-title': messageHasTitle}]">
              <template v-if="messageHasTitle">
                <h6 v-if="options.html" class="dg-title" v-html="messageTitle"></h6>
                <h6 v-else class="dg-title">{{ messageTitle }}</h6>
              </template>

              <div v-if="options.html" class="dg-content" v-html="messageBody"></div>
              <div v-else class="dg-content">{{ messageBody }}</div>

              <form v-if="isHardConfirm || isPrompt"
                    class="dg-form"
                    autocomplete="off"
                    @submit.prevent="submitDialogForm"
              >
                <label for="dg-input-elem" style="font-size: 13px">{{ isPrompt ? promptHelpText : hardConfirmHelpText }}</label>
                <textarea
                  type="text"
                  :placeholder="isPrompt ? '' : options.verification"
                  v-model="input"
                  autocomplete="off"
                  id="dg-input-elem"
                  ref="inputElem"
                  style="
            width: 100%;
            margin-top: 10px;
            height: 80px;
            padding: 5px;
            font-size: 16px;
            border-radius: 4px;
            border: 2px solid #eee;
          "
                ></textarea>

                <div v-if="options.afterText" v-text="options.afterText"></div>
                <label v-if="options.checkbox" style="padding: 7px 2px"
                ><input type="checkbox" v-model="checked" checked="checked" />
                  {{ options.checkboxLabel }}</label
                >
              </form>

            </div>

            <div class="dg-content-footer" :class="customClass.footer">
              <component @click="clickLeftBtn()"
                      :is="leftBtnComponent"
                      :btn-state="leftBtnState"
                      :loading="loading"
                      :class="customClass.cancel"
                      :visible="leftBtnVisible"
                      :options="options"
                      :focus="leftBtnFocus"
              >
                <span v-if="options.html" v-html="leftBtnText"></span>
                <span v-else>{{ leftBtnText }}</span>
              </component>

              <component @click="clickRightBtn()"
                      :is="rightBtnState.component"
                      :btn-state="rightBtnState"
                      :loading="loading"
                      :class="customClass.ok"
                      :visible="rightBtnVisible"
                      :options="options"
                      :focus="rightBtnFocus"
              >
                <span v-if="options.html" v-html="rightBtnText"></span>
                <span v-else>{{ rightBtnText }}</span>
              </component>

              <div class="dg-clear"></div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import OkBtn from './OkButton.vue';
import CancelBtn from './CancelButton.vue';
import {
  ANIMATION_TYPES,
  CONFIRM_TYPES,
  DIALOG_TYPES,
  CUSTOM_CLASS
} from "../constants";
import type {ButtonStateInterface, DialogWindowOptions} from "./../interface";

export default defineComponent({
  name: "DialogWindow",
  data: function () {
    return {
      input: '',
      show: true,
      loading: false,
      closed: false,
      checked: true,
      customClass: Object.assign({}, CUSTOM_CLASS),
      endedAnimations: []
    }
  },
  props: {
    id: {
      type: String,
      required: true
    },
    options: {
      type: Object as PropType<DialogWindowOptions>,
      required: true
    },
    escapeKeyClose: {
      type: Boolean,
      "default": false
    }
  },
  watch: {
    "escapeKeyClose": function(val) {
      if(val === true){
        this.cancelBtnDisabled ? this.proceed() : this.cancel()
      }
    }
  },
  computed: {
    animation(){
      let a = this.options.animation.toUpperCase()
      return Object.prototype.hasOwnProperty.call(ANIMATION_TYPES, a)
          ? ANIMATION_TYPES[a]
          : ANIMATION_TYPES.ZOOM
    },
    loaderEnabled(){
      return this.options.loader
    },
    isHardConfirm(){
      return this.options.window === DIALOG_TYPES.CONFIRM
          && this.options.type === CONFIRM_TYPES.HARD
    },
    isPrompt(){
      return (this.options.window === DIALOG_TYPES.PROMPT)
    },
    leftBtnComponent(){
      return !this.options.reverse ? CancelBtn : OkBtn
    },
    rightBtnComponent(){
      return this.options.reverse ? CancelBtn : OkBtn
    },
    hardConfirmHelpText(){
      return this.options.verificationHelp
          .replace(/\[\+:(\w+)]/g, (match, $1) => {
            return this.options[$1] || match
          })
    },
    promptHelpText () {
      return this.options.promptHelp
          .replace(/\[\+:(\w+)]/g, (match, $1) => {
            return this.options[$1] || match
          })
    },

    // Refactored from MessageMixin
    messageHasTitle(): boolean {
      return Boolean(this.options.message?.title)
    },
    messageTitle(): string | null {
      return this.messageHasTitle ? this.options.message.title : null
    },
    messageBody(){
      return this.options.message.body
    },
    // END - Refactored from MessageMixin

    // Refactored from ButtonMixin
    cancelBtnDisabled () {
      return (this.options.window === DIALOG_TYPES.ALERT)
    },
    okBtnDisabled () {
      return (this.options.window === DIALOG_TYPES.CONFIRM) &&
          (this.options.type === CONFIRM_TYPES.HARD) &&
          (this.input !== this.options.verification)
    },
    leftBtnVisible () {
      return !this.cancelBtnDisabled || this.options.reverse
    },
    rightBtnVisible () {
      return !this.cancelBtnDisabled || !this.options.reverse
    },
    leftBtnFocus () {
      return !this.isHardConfirm && this.options.reverse
    },
    rightBtnFocus () {
      return !this.isHardConfirm && !this.options.reverse
    },
    leftBtnText () {
      return this.options.reverse ? this.options.okText : this.options.cancelText
    },
    rightBtnText () {
      return this.options.reverse ? this.options.cancelText : this.options.okText
    },
    // END - Refactored from ButtonMixin

    btnState(): Pick<ButtonStateInterface, 'loading' | 'options'> {
      return {
        loading: this.loading,
        options: this.options,
      }
    },
    rightBtnState(): ButtonStateInterface {
      const { reverse } = this.options;
      return {
        ...this.btnState,
        component: reverse ? CancelBtn : OkBtn,
        disabled: this.okBtnDisabled,
        visible: (this.options.window !== DIALOG_TYPES.ALERT) || !reverse
      }
    },
    leftBtnState(): ButtonStateInterface {
      const { reverse } = this.options;
      return {
        ...this.btnState,
        component: !reverse ? CancelBtn : OkBtn,
        disabled: this.cancelBtnDisabled,
        visible: !this.cancelBtnDisabled || reverse,
      }
    }
  },
  mounted () {
    this.setCustomClasses()
    ;(this.isHardConfirm || this.isPrompt) && this.$refs.inputElem && this.$refs.inputElem.focus()
  },
  methods: {
    closeAtOutsideClick() {
      if (this.options.backdropClose) {
        this.cancel()
      }
    },
    clickRightBtn () {
      this.options.reverse ? this.cancel() : this.proceed(this.getDefaultData())
    },
    clickLeftBtn () {
      this.options.reverse ? this.proceed(this.getDefaultData()) : this.cancel()
    },
    submitDialogForm () {
      this.okBtnDisabled || this.proceed(this.getDefaultData())
    },
    getDefaultData () {
      return this.isPrompt
        ? {
          message: this.input,
          checked: this.checked ? this.options.checkboxValue : ''
        }
        : null;
    },
    proceed(withData = null){
      if (this.loaderEnabled) {
        this.switchLoadingState(true)
        this.options.promiseResolver({
          close: this.close,
          data: withData
        })
      } else {
        this.options.promiseResolver({
          data: withData
        })
        this.close()
      }
    },
    cancel(){
      if (this.loading) return
      this.close()
    },
    switchLoadingState(loading = null){
      if(loading === null){
        loading = !this.loading
      }

      this.loading = !!loading
    },
    close(){
      this.show = false
      this.closed = true
    },
    animationEnded(type){
      this.endedAnimations.push(type)

      if(this.endedAnimations.indexOf('backdrop') !== -1
          && this.endedAnimations.indexOf('content') !== -1
      ){
        this.options.promiseResolver({ canceled: true })
        this.$emit('close', this.id)
      }
    },
    setCustomClasses(){
      if (Object.prototype.hasOwnProperty.call(this.options, 'customClass')) {
        Object.keys(this.options.customClass).forEach(prop => {
          if (!Object.keys(CUSTOM_CLASS).includes(prop)) {
            console.warn(`[WARNING]: Custom class name "${prop}" is not recognized!`)
          }
        });
      }
      this.customClass = Object.assign(this.customClass, this.options.customClass);
    }
  },
  beforeUnmount(){
    if(!this.closed){
      this.cancelBtnDisabled ? this.proceed() : this.cancel()
    }
  },
  components: {CancelBtn, OkBtn}
})
</script>
